<template>
  <section class="space-y-4">
    <header class="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
      <h3 class="text-[22px] font-black italic uppercase text-[#111]">{{ t('myDeals.title') }}</h3>
      <div class="flex flex-wrap items-center gap-3 w-full sm:w-auto">
        <div v-if="selectedClaimIds.length > 0" class="flex items-center gap-2">
          <button type="button" @click="exportToCsv"
                  :title="t('regionDeals.actions.export') || 'Selectie exporteren'"
                  class="h-10 px-3 flex items-center justify-center gap-2 border border-black bg-black text-white hover:bg-neutral-800 transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <span class="text-xs font-bold uppercase italic">{{ t('myDeals.actions.export') || 'Exporteren (selectie)' }} ({{ selectedClaimIds.length }})</span>
          </button>
          <button type="button" @click="exportAllToCsv"
                  :title="t('myDeals.actions.exportAll') || 'Alles exporteren'"
                  class="h-10 px-3 flex items-center justify-center gap-2 border border-black bg-white text-black hover:bg-neutral-100 transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
            </svg>
            <span class="text-xs font-bold uppercase italic">{{ t('myDeals.actions.exportAll') || 'Alles exporteren' }}</span>
          </button>
        </div>
        <MmSearchInput v-model="query" :placeholder="t('myDeals.searchPlaceholder')" @change="onSearch" />
      </div>
    </header>

    <div v-if="loading" class="text-[15px] text-neutral-600">{{ t('messages.loading') }}</div>
    <div v-else class="w-full">
      <div v-if="error" class="text-[14px] border border-[#cf524e] bg-[#fde2e0] text-[#6b1513] p-3">{{ error }}</div>
      <div v-else class="w-full">
        <template v-if="rows.length">
          <MmTable :columns="columns" :rows="rows" :sort-key="sortKeyBase" :sort-dir="sortDirBase" @sort-change="toggleSort"
                   :selectable="true" :selected-rows="selectedClaimIds" :all-selected="allPageSelected" @select-row="toggleClaimSelection" @select-all="toggleAllPageSelection">
            <template #cell:material="{ row }">
              <div class="flex items-center gap-3">
                <div class="relative">
                  <img :src="row.thumbnail || row.image || placeholder" alt="" class="h-16 w-16 object-cover border border-neutral-300 bg-[#f9f4ec]"/>
                  <div v-if="isRequested(row)" class="absolute top-0 right-0 bg-black text-white text-[8px] px-1 py-0.5 font-bold uppercase">Vraag</div>
                </div>
                <div class="min-w-0">
                  <div class="font-semibold text-neutral-900 truncate">{{ decodeHTML(row.title) }}</div>
                </div>
              </div>
            </template>
            <template #cell:date="{ row }">{{ formatDate(row.date) }}</template>
            <template #cell:materialId="{ row }"><span>{{ padId(row.materialId) }}</span></template>
            <template #cell:waarde="{ row }">{{ formatCurrency(row.waarde) }}</template>
            <template #cell:status="{ row }">
              <span class="inline-block rounded-sm px-2 py-1 text-xs font-bold uppercase tracking-widest" :class="statusClass(row.status)">{{ prettyStatus(row.status) }}</span>
            </template>
            <template #cell:claimedBy="{ row }">
              <div class="min-w-0">
                <div class="truncate">{{ row.claimedBy?.name || '—' }}</div>
                <div class="text-[12px] text-neutral-600 truncate">{{ row.claimedBy?.email || '' }}</div>
              </div>
            </template>
            <template #cell:actions="{ row }">
              <div class="flex items-center gap-2">
                <button type="button" @click="openDetails(row)" class="inline-flex items-center justify-center px-3 py-2 border border-black bg-white hover:bg-black hover:text-white text-sm font-bold uppercase italic">{{ t('buttons.details') }}</button>
                <a v-if="String(row.status||'').toLowerCase() === 'completed'" :href="downloadUrl(row.materialId)" class="inline-flex items-center justify-center px-3 py-2 border border-black bg-white text-black hover:bg-black hover:text-white text-sm font-bold uppercase italic" target="_blank">PDF</a>
              </div>
            </template>
          </MmTable>

          <MmPagination :page="page" :pages="pages" @page-change="goTo" />
        </template>
        <div v-else class="text-[15px] text-neutral-700">{{ t('myDeals.empty') }}</div>
      </div>
    </div>

    <!-- Details modal -->
    <MmModal v-model="openDetailsModal" :title="t('myDeals.modal.title')">
      <div v-if="current" class="space-y-4">
        <div class="flex gap-4 items-start">
          <img :src="current.image || placeholder" alt="" class="w-24 h-24 object-cover border border-neutral-300 bg-[#f9f4ec]"/>
          <div class="min-w-0 flex-1">
            <h4 class="m-0 font-semibold">{{ current.title }}</h4>
            <p class="text-sm text-neutral-700 m-0">{{ current.teaser }}</p>
            <dl class="mt-3 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-sm">
              <dt class="text-neutral-600">{{ t('regionDeals.modal.claimNumber') }}</dt><dd><span>{{ current.claimNumber || '—' }}</span></dd>
            <dt class="text-neutral-600">{{ t('myDeals.modal.waarde') }}</dt><dd>{{ formatCurrency(current.waarde) }}</dd>
            <dt class="text-neutral-600">{{ t('regionDeals.modal.adId') }}</dt><dd><span>{{ padId(current.materialId) || '—' }}</span></dd>
            <dt class="text-neutral-600">{{ t('materials.type') || 'Type' }}</dt>
            <dd>
              <span v-if="isRequested(current)" class="bg-black text-white px-2 py-0.5 text-[10px] font-black uppercase italic tracking-wider rounded-full">
                {{ t('materials.requested') || 'Gevraagd' }}
              </span>
              <span v-else class="bg-[#E7D7BF] text-black px-2 py-0.5 text-[10px] font-black uppercase italic tracking-wider rounded-full">
                {{ t('materials.offered') || 'Aangeboden' }}
              </span>
            </dd>
            <dt class="text-neutral-600">{{ t('myDeals.modal.claimDate') }}</dt><dd>{{ formatDate(current.date) }}</dd>
              <dt class="text-neutral-600">{{ t('regionDeals.modal.status') }}</dt>
              <dd>
                <span
                  class="inline-block rounded-sm px-2 py-1 text-xs font-bold uppercase tracking-widest"
                  :class="statusClass(current.status)"
                >
                  {{ prettyStatus(current.status) }}
                </span>
              </dd>
              <template v-if="String(current.status||'').toLowerCase()==='cancelled' && current.releaseDate">
                <dt class="text-neutral-600">{{ t('regionDeals.modal.cancelledOn') }}</dt>
                <dd>{{ formatDate(current.releaseDate) }}</dd>
              </template>
              <dt class="text-neutral-600">{{ t('regionDeals.modal.claimedBy') }}</dt>
              <dd>
                {{ current.claimedBy?.name }}
                <template v-if="current.claimedBy?.email"><span class="text-neutral-600"> (<a class="text-neutral-600 underline" :href="'mailto:' + current.claimedBy.email">{{ current.claimedBy.email }}</a>)</span></template>
              </dd>
            </dl>
            <div v-if="String(current.status||'').toLowerCase() === 'completed'" class="mt-4">
              <a :href="downloadUrl(current.materialId)" class="inline-flex items-center justify-center px-4 py-2 border border-black bg-black text-white hover:bg-white hover:text-black text-sm font-bold uppercase italic" target="_blank">
                {{ t('buttons.downloadPakbon') || 'Download Pakbon' }}
              </a>
            </div>
          </div>
        </div>

        <div v-if="isBedrijf && !isCancelled" class="border-t border-neutral-200 pt-3">
          <h5 class="font-semibold mb-2">{{ t('myDeals.modal.updateStatus') }}</h5>
          <div v-if="current.materialExists === false" class="bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800">
            {{ t('myDeals.modal.materialDeleted') || 'Claims waarvan de materialen niet meer bestaan kunnen niet aangepast worden.' }}
          </div>
          <div v-else class="flex items-center gap-2 text-[14px] relative">
            <select v-model="selectedStatus"
                    class="h-10 px-3 pr-8 border border-black bg-white text-sm font-bold uppercase italic focus:ring-0 focus:border-black appearance-none w-full"
                    :disabled="isCancelled">
              <option value="pending">{{ t('regionDeals.status.pending') }}</option>
              <option value="rejected">{{ t('regionDeals.status.rejected') }}</option>
              <option value="completed">{{ t('regionDeals.status.completed') }}</option>
            </select>
            <svg class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" fill="none"
                 stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
            <button type="button" class="mm-modal__button text-[15px] shrink-0" :disabled="isCancelled || saving || !hasStatusChanged" @click="saveStatus">{{ t('buttons.save') }}</button>
          </div>
        </div>

        <div v-if="isBedrijf && !isCancelled" class="border-t border-neutral-200 pt-3">
          <div class="mm-admin-block space-y-2">
            <h5 class="text-black font-black italic uppercase text-[15px] mb-2">{{ t('myDeals.modal.claimAdmin') }}</h5>
            <!-- Subkop voor reden vrijgeven -->
            <h6 id="release-reason-heading" class="text-black font-semibold text-[15px]">{{ t('myDeals.modal.releaseReasonTitle') }}</h6>
            <template v-if="current.materialExists !== false">
              <textarea
                id="release-reason"
                v-model="releaseReason"
                rows="3"
                class="mm-admin-block__textarea w-full p-2 text-[15px] focus:border-black focus:ring-0"
                :disabled="isCancelled"
                :aria-labelledby="'release-reason-heading'"
                :placeholder="t('myDeals.modal.releaseReasonPlaceholder')"></textarea>
              <div class="flex flex-wrap gap-2 items-center">
                <button type="button" class="mm-admin-block__button" :disabled="isCancelled || saving || !current?.claimedBy" @click="confirmRelease">{{ t('myDeals.modal.release') }}</button>
                <span v-if="!current?.claimedBy" class="text-[13px] text-neutral-600">{{ t('myDeals.modal.notClaimed') }}</span>
              </div>
            </template>
            <div v-else class="text-[13px] text-neutral-600 italic">
              {{ t('myDeals.modal.materialDeletedAdmin') || 'Dit materiaal bestaat niet meer, de claim kan niet worden vrijgegeven.' }}
            </div>
            <div v-if="current?.status==='cancelled' && current?.releaseReason" class="text-[13px] text-neutral-700">
              <strong>{{ t('myDeals.modal.releasedWithReason') }}</strong>
              <span class="whitespace-pre-line">{{ current.releaseReason }}</span>
            </div>
          </div>
        </div>
      </div>
    </MmModal>
  </section>

</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import WpApiClient from '../lib/wp-api.js'
import MmTable from './MmTable.vue'
import MmSearchInput from './MmSearchInput.vue'
import MmPagination from './MmPagination.vue'
import MmModal from './MmModal.vue'
import { statusBadgeClass } from '../lib/status-utils'
import { formatDateNL, padId as padIdUtil, decodeHTML } from '../lib/format-utils'

const props = defineProps({
  embedded: { type: Boolean, default: false },
  t: { type: Function, default: (key) => key }
})

const t = props.t

const api = new WpApiClient()
const loading = ref(true)
const error = ref('')
const items = ref([])
const total = ref(0)
const page = ref(1)
const perPage = ref(10)
const query = ref('')
const sortKey = ref('date_desc')

const openDetailsModal = ref(false)
const current = ref(null)
const saving = ref(false)
const selectedClaimIds = ref([])
const releaseReason = ref('')
const releasing = ref(false)
const selectedStatus = ref('pending')

const placeholder = 'data:image/svg+xml;utf8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><rect width="80" height="80" fill="#f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#9ca3af" font-family="sans-serif" font-size="10">—</text></svg>`)

const isBedrijf = computed(() => {
  const rt = (window.materiaalMaatjesData?.user?.roleType || '').toLowerCase()
  return rt === 'bedrijf' || rt === 'school'
})

const columns = computed(() => {
  const cols = [
    { key: 'material', label: props.t('regionDeals.columns.material'), sortable: true },
    { key: 'claimNumber', label: props.t('regionDeals.columns.claimNumber'), sortable: true },
    { key: 'materialId', label: props.t('regionDeals.modal.adId'), sortable: true },
    { key: 'waarde', label: props.t('myDeals.columns.waarde'), sortable: true },
    { key: 'date', label: props.t('myDeals.columns.claimDate'), sortable: true },
    { key: 'status', label: props.t('myDeals.columns.claimStatus'), sortable: true },
    { key: 'claimedBy', label: props.t('regionDeals.columns.claimedBy'), sortable: false },
  ]
  cols.push({ key: 'actions', label: props.t('regionDeals.columns.actions'), sortable: false })
  return cols
})

const rows = computed(() => items.value)
const allPageSelected = computed(() => {
  if (!rows.value.length) return false
  return rows.value.every(r => selectedClaimIds.value.includes(r.id))
})
const pages = computed(() => {
  const p = Math.max(1, Math.ceil(total.value / perPage.value))
  return Array.from({ length: p }, (_, i) => i + 1)
})

const sortKeyBase = computed(() => (String(sortKey.value).split('_')[0] || 'date'))
const sortDirBase = computed(() => (String(sortKey.value).split('_')[1] || 'desc'))

function toggleSort(key){
  const base = String(key)
  const currentBase = sortKeyBase.value
  const currentDir = sortDirBase.value
  if (base === currentBase) {
    sortKey.value = `${base}_${currentDir === 'asc' ? 'desc' : 'asc'}`
  } else {
    sortKey.value = `${base}_asc`
  }
  reload()
}

function goTo(p){ page.value = p; reload() }
function onSearch(){ page.value = 1; reload() }

function toggleClaimSelection(id, selected){
  if (selected) {
    if (!selectedClaimIds.value.includes(id)) selectedClaimIds.value.push(id)
  } else {
    selectedClaimIds.value = selectedClaimIds.value.filter(x => x !== id)
  }
}
function toggleAllPageSelection(selected){
  if (selected) {
    rows.value.forEach(r => {
      if (!selectedClaimIds.value.includes(r.id)) selectedClaimIds.value.push(r.id)
    })
  } else {
    const pageIds = rows.value.map(r => r.id)
    selectedClaimIds.value = selectedClaimIds.value.filter(id => !pageIds.includes(id))
  }
}

async function exportToCsv(){
  if (!selectedClaimIds.value.length) return
  loading.value = true
  try {
    const roleType = (window.materiaalMaatjesData?.user?.roleType || '').toLowerCase()
    const isBedrijfLocal = roleType === 'bedrijf'
    let res
    const params = { page: 1, perPage: 9999, search: query.value, sort: sortKey.value, nonce: window?.materiaalAjax?.nonce }
    if (isBedrijfLocal) {
      res = await api.listMyClaimsCompany(params)
    } else {
      res = await api.listMyClaims(params)
    }
    if (res && res.ok && Array.isArray(res.claims)) {
      const allItems = res.claims.map(normalizeDeal)
      const selectedItems = allItems.filter(it => selectedClaimIds.value.includes(it.id))
      doExport(selectedItems, 'my-claims-export')
    }
  } catch (e) {
    console.error('Export selection failed', e)
  } finally {
    loading.value = false
  }
}

async function exportAllToCsv(){
  loading.value = true
  try {
    const roleType = (window.materiaalMaatjesData?.user?.roleType || '').toLowerCase()
    const isBedrijfLocal = roleType === 'bedrijf'
    let res
    const params = { page: 1, perPage: 9999, search: query.value, sort: sortKey.value, nonce: window?.materiaalAjax?.nonce }
    if (isBedrijfLocal) {
      res = await api.listMyClaimsCompany(params)
    } else {
      res = await api.listMyClaims(params)
    }
    if (res && res.ok && Array.isArray(res.claims)) {
      doExport(res.claims.map(normalizeDeal), 'all-my-claims')
    }
  } catch (e) {
    console.error('Export all failed', e)
  } finally {
    loading.value = false
  }
}

function doExport(itemsToExport, filenamePrefix = 'claims-export'){
  if (!itemsToExport.length) return

  const headers = [
    'ID',
    t('regionDeals.modal.adId') || 'Advertentie ID',
    t('regionDeals.columns.claimNumber') || 'Claimnr',
    t('regionDeals.columns.material') || 'Materiaal Titel',
    t('regionDeals.columns.status') || 'Status',
    t('regionDeals.columns.waarde') || 'Waarde',
    t('regionDeals.columns.date') || 'Datum',
    t('regionDeals.modal.cancelledOn') || 'Geannuleerd op',
    t('regionDeals.modal.reason') || 'Reden',
    t('regionDeals.columns.provider') || 'Aanbieder Naam',
    t('regionDeals.csv.providerEmail') || 'Aanbieder Email',
    t('regionDeals.csv.providerPhone') || 'Aanbieder Telefoon',
    t('regionDeals.csv.providerRegions') || 'Aanbieder Regio\'s',
    t('regionDeals.columns.claimedBy') || 'Geclaimd door Naam',
    t('regionDeals.csv.claimerEmail') || 'Claimer Email',
    t('regionDeals.csv.claimerPhone') || 'Claimer Telefoon',
    t('regionDeals.csv.claimerRegions') || 'Claimer Regio\'s'
  ]

  const rowsToExport = itemsToExport.map(it => [
    it.id || '',
    padId(it.materialId || it.id),
    it.claimNumber || '',
    it.title || it.materialTitle || '',
    prettyStatus(it.status),
    formatCurrency(it.waarde),
    formatDate(it.date),
    it.releaseDate ? formatDate(it.releaseDate) : '',
    it.releaseReason || '',
    it.provider?.name || '',
    it.provider?.email || '',
    it.provider?.phone || '',
    (it.provider?.regions || []).join(', '),
    it.claimedBy?.name || '',
    it.claimedBy?.email || '',
    it.claimedBy?.phone || '',
    (it.claimedBy?.regions || []).join(', ')
  ])

  let csvContent = headers.join(';') + '\n'
  rowsToExport.forEach(row => {
    const rowStr = row.map(val => {
      const s = String(val).replace(/"/g, '""')
      return s.includes(';') || s.includes('"') || s.includes('\n') ? `"${s}"` : s
    }).join(';')
    csvContent += rowStr + '\n'
  })

  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', `${filenamePrefix}-${new Date().toISOString().slice(0,10)}.csv`)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

function prettyStatus(s){
  const v = String(s||'').toLowerCase()
  if (v === 'pending' || v === 'new' || v === 'in_progress') return props.t('regionDeals.status.pending')
  if (v === 'approved' || v === 'accepted' || v === 'completed') return props.t('regionDeals.status.completed')
  if (v === 'rejected') return props.t('regionDeals.status.rejected')
  if (v === 'cancelled' || v === 'canceled') return props.t('regionDeals.status.cancelled')
  return s || '—'
}

function statusClass(s){
  return statusBadgeClass(s)
}

function formatDate(iso){
  return formatDateNL(iso)
}

function padId(val){
  return padIdUtil(val)
}

function formatCurrency(val) {
  if (val === null || val === undefined || val === '') return '—'
  const n = Number(val)
  if (isNaN(n)) return val
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n)
}

function downloadUrl(materialId) {
  const baseUrl = window.materiaalMaatjesData?.ajaxUrl || window.materiaalAjax?.ajaxurl || '/wp-admin/admin-ajax.php'
  const nonce = window?.materiaalAjax?.nonce || ''
  return `${baseUrl}?action=materiaal_download_claim_pdf&material_id=${materialId}&nonce=${nonce}`
}

function isRequested(row) {
  if (!row) return false
  const type = String(row.mm_type || row.type || '').toLowerCase()
  return type === 'requested' || type === 'gevraagd'
}

function normalizeDeal(d){
  const o = d || {}
  // Maak nette korte teaser uit beschikbare beschrijving/inhoud
  const rawDesc = (
    o.excerpt || o.summary || o.description || o.content || ''
  ).toString()
  const plain = rawDesc.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
  const teaser = plain.length <= 140 ? plain : (plain.slice(0,140).replace(/\s+\S*$/, '') + '…')
  return {
    id: o.id || o.materialId,
    materialId: o.materialId || o.id,
    materialSlug: o.materialSlug,
    title: o.title || '—',
    excerpt: o.excerpt || '',
    teaser,
    image: o.image || '',
    thumbnail: o.thumbnail || o.image || '',
    claimNumber: o.claimNumber || o.claim_nr || o.claimNo || '',
    date: o.date || '',
    status: o.status || 'new',
    claimedBy: o.claimedBy || null,
    location: o.location || '',
    waarde: o.waarde || '',
    mm_type: o.mm_type || o.type || 'offered',
    releaseReason: o.releaseReason || o.cancelReason || '',
    releaseDate: o.releaseDate || o.cancelledAt || '',
    provider: o.provider || null,
  }
}

async function reload(){
  loading.value = true
  error.value = ''
  try {
    const roleType = (window.materiaalMaatjesData?.user?.roleType || '').toLowerCase()
    const isBedrijfLocal = roleType === 'bedrijf'

    let res
    if (isBedrijfLocal) {
      res = await api.listMyClaimsCompany({ page: page.value, perPage: perPage.value, search: query.value, sort: sortKey.value, nonce: window?.materiaalAjax?.nonce })
    } else {
      res = await api.listMyClaims({ page: page.value, perPage: perPage.value, search: query.value, sort: sortKey.value, nonce: window?.materiaalAjax?.nonce })
    }

    if (res.ok) {
      items.value = Array.isArray(res.claims) ? res.claims.map(normalizeDeal) : []
      total.value = Number(res.total || 0)
    } else {
      items.value = []
      total.value = 0
      error.value = res.error || 'Kon claims niet laden.'
    }
  } catch (e) {
    items.value = []
    total.value = 0
    error.value = 'Laden van claims mislukt. Probeer het later opnieuw.'
  } finally {
    loading.value = false
  }
}

function openDetails(row){
  current.value = { ...row }
  releaseReason.value = row.releaseReason || ''
  // Normaliseer legacy statussen en initialiseeer dropdown
  const v = String(row.status||'').toLowerCase()
  if (v === 'new' || v === 'in_progress') selectedStatus.value = 'pending'
  else if (v === 'accepted' || v === 'approved') selectedStatus.value = 'completed'
  else if (v) selectedStatus.value = v
  else selectedStatus.value = 'pending'
  openDetailsModal.value = true
}

const hasStatusChanged = computed(() => {
  const cur = String(current.value?.status || '').toLowerCase()
  // map legacy to current for comparison
  const normalized = (cur === 'new' || cur === 'in_progress') ? 'pending' : ((cur === 'accepted' || cur === 'approved') ? 'completed' : cur)
  return normalized !== selectedStatus.value
})

// Wanneer de deal reeds geannuleerd is, moet alles in de modal uitgegrijsd en niet aanpasbaar zijn
const isCancelled = computed(() => String(current.value?.status || '').toLowerCase() === 'cancelled')

async function saveStatus(){
  if (!current.value) return
  if (isCancelled.value) return
  if (!hasStatusChanged.value) return
  saving.value = true
  try {
    const next = selectedStatus.value
    const res = await api.updateClaim({ materialId: current.value.materialId, status: next, nonce: window?.materiaalAjax?.nonce })
    if (res && (res.ok || res.success)) {
      current.value.status = next
      const idx = items.value.findIndex(x => (x.materialId||x.id) === current.value.materialId)
      if (idx >= 0) items.value[idx].status = next
    } else {
      alert(res?.error || 'Status opslaan mislukt. Probeer het opnieuw.')
    }
  } catch(e) {
    alert(e?.message || 'Status opslaan mislukt.')
  } finally { saving.value = false }
}

async function confirmRelease(){
  if (!current.value) return
  if (isCancelled.value) return
  if (!releaseReason.value || !releaseReason.value.trim()) {
    alert('Geef een reden op voor het vrijgeven van deze claim.')
    return
  }
  if (!window.confirm('Weet je zeker dat je deze claim wilt vrijgeven? Het materiaal kan daarna opnieuw geclaimd worden.')) return
  saving.value = true
  try {
    const res = await api.releaseClaim(current.value.materialId, { reason: releaseReason.value.trim(), nonce: window?.materiaalAjax?.nonce })
    if (res && (res.ok || res.success)) {
      // Markeer in UI als geannuleerd en laat in de lijst staan voor historie
      const id = current.value.materialId
      current.value.status = 'cancelled'
      current.value.claimedBy = null
      current.value.releaseReason = releaseReason.value.trim()
      // Zet annuleringsdatum direct in de UI
      const nowIso = new Date().toISOString()
      current.value.releaseDate = nowIso
      const idx = items.value.findIndex(x => (x.materialId||x.id) === id)
      if (idx >= 0) {
        items.value[idx].status = 'cancelled'
        items.value[idx].claimedBy = null
        items.value[idx].releaseReason = releaseReason.value.trim()
        items.value[idx].releaseDate = nowIso
      }
      openDetailsModal.value = false // sluit modal na bevestigde vrijgave
    } else {
      alert(res?.error || 'Vrijgeven mislukt. Probeer het opnieuw.')
    }
  } catch(e) {
    alert(e?.message || 'Vrijgeven mislukt.')
  } finally {
    saving.value = false
  }
}

onMounted(() => reload())
</script>

<style scoped>
.mm-admin-block {
  background-color: #EEDCC5 !important;
  border: 1px solid #000 !important;
  padding: 12px !important;
  border-radius: 6px !important;
}
.mm-admin-block__textarea {
  background-color: #fff !important;
  border: 1px solid #d4d4d4 !important;
  border-radius: 6px !important;
  color: #000 !important;
}
.mm-admin-block__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid #000;
  background-color: #fff;
  color: #000;
  font-size: 15px;
}
.mm-admin-block__button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
